import { graphql, useFragment } from 'react-relay';
import { hasKey } from 'dibs-ts-utils/exports/hasKey.ts';

import {
    type useDetailEntries_item$key,
    type useDetailEntries_item$data,
} from './__generated__/useDetailEntries_item.graphql.ts';

/**
 * if config field is an array, then only use the first detail that has a value
 * e.g. ['DATE_OF_MANUFACTURE', 'PERIOD'] will use the first detail that has a value
 */

const FIELD_CONFIG = {
    F: ['SET_SIZE', 'CONDITION', ['DATE_OF_MANUFACTURE', 'PERIOD'], 'DIMENSIONS'],
    F_RUG: ['CONDITION', ['DATE_OF_MANUFACTURE', 'PERIOD'], 'DIMENSIONS'],
    A: ['SET_SIZE', 'CONDITION', ['DATE_OF_MANUFACTURE', 'PERIOD'], 'DIMENSIONS'],
    V: ['CONDITION', ['DATE_OF_MANUFACTURE', 'PERIOD']],
    J: ['CONDITION', 'WEIGHT', ['DATE_OF_MANUFACTURE', 'PERIOD']],
};

const itemFragment = graphql`
    fragment useDetailEntries_item on Item {
        categoryL2: getCategoriesByLevel(level: 2) {
            key
        }
        pdpDetailsDisplay {
            detailKey
            detailValues {
                __typename
            }
        }
    }
`;

const hasDetail = (item: useDetailEntries_item$data, entry: string): boolean => {
    const detail = item?.pdpDetailsDisplay?.find(
        detailsDisplay => detailsDisplay?.detailKey === entry
    );
    return !!detail?.detailValues?.length;
};

export function useDetailEntries({ item: itemRef }: { item: useDetailEntries_item$key }): string[] {
    const item = useFragment(itemFragment, itemRef);
    const categoryL2Key = item?.categoryL2?.[0]?.key;
    const vertical = categoryL2Key?.split('_')?.[0];

    if (!categoryL2Key || !vertical) return [];

    const categoryL2KeyConfig = hasKey(FIELD_CONFIG, categoryL2Key)
        ? FIELD_CONFIG[categoryL2Key]
        : null;
    const verticalConfig = hasKey(FIELD_CONFIG, vertical) ? FIELD_CONFIG[vertical] : null;

    const detailEntries = categoryL2KeyConfig ?? verticalConfig;

    if (!detailEntries) return [];

    const filteredDetailEntries = detailEntries.flatMap(entry => {
        if (Array.isArray(entry)) {
            const foundDetail = entry.find(subEntry => hasDetail(item, subEntry));
            return foundDetail ? [foundDetail] : [];
        }
        return hasDetail(item, entry) ? [entry] : [];
    });

    return filteredDetailEntries;
}
